import React, { useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getTimezoneOptions, getDetectedTimezone } from '../utils/timezone';

export interface TimezoneOption {
  value: string;
  label: string;
}

interface TimezoneSelectProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
  useDetectedAsDefault?: boolean;
  id?: string;
}

const TimezoneSelect: React.FC<TimezoneSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  size = 'small',
  useDetectedAsDefault = true,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'timezoneSelect' });
  const options = useMemo(() => getTimezoneOptions(), []);
  const detected = useMemo(() => getDetectedTimezone(), []);

  const effectiveValue = (value && value.trim()) || (useDetectedAsDefault ? detected : '');

  const selectedOption = useMemo(() => {
    if (!effectiveValue) return null;
    return options.find((tz) => tz.value === effectiveValue) ?? null;
  }, [options, effectiveValue]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.label}
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(newValue ? newValue.value : null);
      }}
      disabled={disabled}
      isOptionEqualToValue={(option, val) => !!val && option.value === val.value}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          placeholder={placeholder ?? t('selectTimezone')}
        />
      )}
      slotProps={{
        popper: { style: { zIndex: 2000 } },
        paper: { style: { zIndex: 2000 } }
      }}
    />
  );
};

export default TimezoneSelect;
